import { useEffect, useState, useCallback, useRef, createContext, useContext, ReactNode } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export type TourStep = {
  id: string;
  target?: string;
  title: string;
  body: ReactNode;
  placement?: "top" | "bottom" | "left" | "right" | "center";
};

export type Tour = {
  id: string;
  steps: TourStep[];
};

type TourContextValue = {
  startTour: (tour: Tour, opts?: { force?: boolean }) => void;
  endTour: () => void;
  activeTourId: string | null;
};

const TourContext = createContext<TourContextValue | null>(null);

const SEEN_KEY_PREFIX = "dd_tour_seen_";

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error("useTour must be used inside TourProvider");
  return ctx;
}

export function TourProvider({ children }: { children: ReactNode }) {
  const [tour, setTour] = useState<Tour | null>(null);
  const [stepIdx, setStepIdx] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);

  const startTour = useCallback((t: Tour, opts?: { force?: boolean }) => {
    if (!opts?.force) {
      try { if (localStorage.getItem(SEEN_KEY_PREFIX + t.id) === "1") return; } catch {}
    }
    setStepIdx(0);
    setTour(t);
  }, []);

  const endTour = useCallback(() => {
    if (tour) {
      try { localStorage.setItem(SEEN_KEY_PREFIX + tour.id, "1"); } catch {}
    }
    setTour(null);
    setRect(null);
  }, [tour]);

  // Track target element rect on each step / resize / scroll
  const step = tour?.steps[stepIdx];
  useEffect(() => {
    if (!step) return;
    if (!step.target || step.placement === "center") {
      setRect(null);
      return;
    }
    const update = () => {
      const el = document.querySelector(step.target!) as HTMLElement | null;
      if (!el) { setRect(null); return; }
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      // Defer a frame so layout settles
      requestAnimationFrame(() => {
        const r = el.getBoundingClientRect();
        setRect(r);
      });
    };
    update();
    const t1 = setTimeout(update, 350); // after smooth scroll
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      clearTimeout(t1);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [step]);

  const next = () => {
    if (!tour) return;
    if (stepIdx >= tour.steps.length - 1) endTour();
    else setStepIdx(i => i + 1);
  };
  const prev = () => setStepIdx(i => Math.max(0, i - 1));

  return (
    <TourContext.Provider value={{ startTour, endTour, activeTourId: tour?.id || null }}>
      {children}
      {tour && step && createPortal(
        <TourOverlay
          tour={tour}
          step={step}
          stepIdx={stepIdx}
          rect={rect}
          onNext={next}
          onPrev={prev}
          onSkip={endTour}
        />,
        document.body
      )}
    </TourContext.Provider>
  );
}

function TourOverlay({
  tour, step, stepIdx, rect, onNext, onPrev, onSkip,
}: {
  tour: Tour;
  step: TourStep;
  stepIdx: number;
  rect: DOMRect | null;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
}) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const isCenter = !rect || step.placement === "center";
  const padding = 8;

  // Compute popover position relative to target
  const popoverStyle: React.CSSProperties = {};
  if (isCenter) {
    popoverStyle.top = "50%";
    popoverStyle.left = "50%";
    popoverStyle.transform = "translate(-50%, -50%)";
  } else if (rect) {
    const placement = step.placement || (rect.top > 200 ? "top" : "bottom");
    const popW = 360;
    const popH = popoverRef.current?.offsetHeight ?? 180;
    if (placement === "top") {
      popoverStyle.top = Math.max(16, rect.top - popH - 12);
      popoverStyle.left = Math.min(window.innerWidth - popW - 16, Math.max(16, rect.left + rect.width / 2 - popW / 2));
    } else if (placement === "bottom") {
      popoverStyle.top = Math.min(window.innerHeight - popH - 16, rect.bottom + 12);
      popoverStyle.left = Math.min(window.innerWidth - popW - 16, Math.max(16, rect.left + rect.width / 2 - popW / 2));
    } else if (placement === "left") {
      popoverStyle.top = Math.max(16, rect.top + rect.height / 2 - popH / 2);
      popoverStyle.left = Math.max(16, rect.left - popW - 12);
    } else {
      popoverStyle.top = Math.max(16, rect.top + rect.height / 2 - popH / 2);
      popoverStyle.left = Math.min(window.innerWidth - popW - 16, rect.right + 12);
    }
  }

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none" data-testid="tour-overlay">
      {/* Dimmer */}
      <div
        className="absolute inset-0 bg-black/60 transition-opacity pointer-events-auto"
        onClick={onSkip}
      />
      {/* Spotlight ring */}
      {rect && !isCenter && (
        <div
          className="absolute rounded-lg pointer-events-none transition-all"
          style={{
            top: rect.top - padding,
            left: rect.left - padding,
            width: rect.width + padding * 2,
            height: rect.height + padding * 2,
            boxShadow: "0 0 0 4px rgba(232,114,42,0.65), 0 0 0 9999px rgba(0,0,0,0.0)",
            outline: "2px solid #e8722a",
            outlineOffset: "0px",
          }}
        />
      )}
      {/* Popover card */}
      <div
        ref={popoverRef}
        className="absolute pointer-events-auto bg-[#161616] border border-[#2A2A3A] rounded-xl shadow-2xl p-5 w-[360px] max-w-[calc(100vw-32px)]"
        style={popoverStyle}
        data-testid="tour-popover"
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3 className="text-sm font-semibold text-white" data-testid="tour-step-title">{step.title}</h3>
          <button
            onClick={onSkip}
            className="p-1 rounded hover:bg-white/5 text-[#71717a] hover:text-white shrink-0"
            data-testid="button-tour-skip"
            aria-label="Skip tour"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="text-[13px] text-[#a1a1aa] leading-relaxed mb-4" data-testid="tour-step-body">
          {step.body}
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-mono text-[#52525b]" data-testid="tour-step-counter">
            {stepIdx + 1} / {tour.steps.length}
          </span>
          <div className="flex items-center gap-1.5">
            {stepIdx > 0 && (
              <Button size="sm" variant="ghost" onClick={onPrev} className="h-7 text-xs" data-testid="button-tour-prev">
                <ChevronLeft className="w-3.5 h-3.5 mr-0.5" /> Back
              </Button>
            )}
            <Button
              size="sm"
              onClick={onNext}
              className="h-7 text-xs bg-brand-orange hover:bg-brand-orange/90 text-white"
              data-testid="button-tour-next"
            >
              {stepIdx >= tour.steps.length - 1 ? "Done" : "Next"}
              {stepIdx < tour.steps.length - 1 && <ChevronRight className="w-3.5 h-3.5 ml-0.5" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
